import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddPaymentModalStripe from "@/components/Payments/AddPaymentModalStripe";
import PaymentMethodsModal from "@/components/Introduction/PaymentMethodsModal";
import { fetchPaymentMethods } from "@/actions/payments";
import { createSubscriptionPlan } from "@/services/PlanService";
import { useKeepGitchaPro, useRefreshGitchaPro, useStartGitchaProTrial } from "@/hooks/useGitchaPro";

const PRO_MEMBER_PLAN = "Pro Member";
const MONTHLY_PERIOD = 2;
const AGENT_USER_TYPE = 2;

const useGitchaProCheckout = ({ show, onTrialStarted, onKept, onUpgraded }) => {
  const dispatch = useDispatch();
  const storedCards = useSelector((state) => state.paymentMethods.paymentMethods);
  const paymentMethods = useMemo(() => (Array.isArray(storedCards) ? storedCards : []), [storedCards]);
  const refreshPro = useRefreshGitchaPro();
  const [billing, setBilling] = useState(false);
  const [card, setCard] = useState(null);
  const [addingCard, setAddingCard] = useState(false);
  const [pickingCard, setPickingCard] = useState(false);
  const [error, setError] = useState(null);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    if (!show) {
      setBilling(false);
      setError(null);
    }
  }, [show]);

  useEffect(() => {
    if (billing) dispatch(fetchPaymentMethods());
  }, [billing, dispatch]);

  const loadCards = useCallback(() => dispatch(fetchPaymentMethods()), [dispatch]);

  useEffect(() => {
    setCard((current) => {
      if (current && paymentMethods.some((item) => item.Payment_Mode_Id === current.Payment_Mode_Id)) {
        return current;
      }
      return paymentMethods.find((item) => item.IsDefault === 1) || paymentMethods[0] || null;
    });
  }, [paymentMethods]);

  const startTrial = useStartGitchaProTrial({
    onSuccess: (next) => onTrialStarted(next),
    onError: (err) => setError(err.message),
  });
  const keepPro = useKeepGitchaPro({
    onSuccess: (next) => onKept(next),
    onError: (err) => setError(err.message),
  });

  const upgradeNow = async (period = MONTHLY_PERIOD) => {
    setError(null);
    setUpgrading(true);
    try {
      const response = await createSubscriptionPlan({
        PlanName: PRO_MEMBER_PLAN,
        PlanPeriod: period,
        PaymentMethodId: card?.CardId,
        PaymentModeId: card?.Payment_Mode_Id,
        UserType: AGENT_USER_TYPE,
      });
      if (response?.errorCode === 0) {
        refreshPro();
        onUpgraded();
      } else {
        setError(response?.message || "Could not upgrade. Please try again.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Could not upgrade. Please try again.");
    } finally {
      setUpgrading(false);
    }
  };

  const modals = (
    <>
      <AddPaymentModalStripe
        show={addingCard}
        onHide={() => setAddingCard(false)}
        updateReloadParam={() => dispatch(fetchPaymentMethods())}
        fromSubscriptionComponent
      />
      <PaymentMethodsModal
        show={pickingCard}
        paymentMethods={paymentMethods}
        selectedCardId={card?.Payment_Mode_Id}
        updateCardParam={(next) => setCard(next)}
        onHide={() => setPickingCard(false)}
        AddNewPayment={() => {
          setPickingCard(false);
          setAddingCard(true);
        }}
      />
    </>
  );

  return {
    billing,
    setBilling,
    card,
    error,
    setError,
    startTrial,
    keepPro,
    upgradeNow,
    upgrading,
    busy: startTrial.isPending || keepPro.isPending || upgrading,
    covered: addingCard || pickingCard,
    changeCard: () => setPickingCard(true),
    addCard: () => setAddingCard(true),
    loadCards,
    modals,
  };
};

export default useGitchaProCheckout;
